import { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '../../api/client'
import { Modal, Button, StatusBadge } from '../ui'
import type { BadgeColor } from '../ui'
import type { GlobalAnnouncementUserDto } from '../../api/generated/dtos'
import { CheckCircle, Megaphone } from 'lucide-react'

const TYPE_BADGE: Record<string, { label: string; color: BadgeColor }> = {
  Info: { label: 'Informazione', color: 'info' },
  Feature: { label: 'Novità', color: 'primary' },
  Update: { label: 'Aggiornamento', color: 'secondary' },
  Maintenance: { label: 'Manutenzione', color: 'warning' },
  Security: { label: 'Sicurezza', color: 'danger' },
}

// Mostra in coda (uno alla volta) gli annunci globali con ShowAsPopup=true non ancora letti
// dall'utente corrente. Chiudere con la X non conta come conferma (l'annuncio ricompare al
// prossimo accesso finché non si preme "Ho letto"): ShowAsPopup/RequireAcknowledgment erano
// configurabili dall'Admin ma nessuna pagina li mostrava mai a un utente reale.
export function GlobalAnnouncementPopup() {
  const queryClient = useQueryClient()
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set())

  const { data: announcements = [] } = useQuery<GlobalAnnouncementUserDto[]>({
    queryKey: ['global-announcements', 'pending-popup'],
    queryFn: () => apiClient.get('/global-announcements/pending-popup').then((r) => r.data),
    staleTime: 5 * 60 * 1000,
  })

  const acknowledgeMutation = useMutation({
    mutationFn: (id: string) => apiClient.post(`/global-announcements/${id}/acknowledge`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['global-announcements', 'pending-popup'] })
    },
  })

  const queue = announcements.filter((a) => !dismissedIds.has(a.id))
  const current = queue[0]

  // Se la query viene invalidata mentre il popup è chiuso (navigazione), evita di riaprirsi
  // per un annuncio già scartato in questa sessione.
  useEffect(() => {
    if (dismissedIds.size === 0) return
    setDismissedIds((prev) => new Set([...prev].filter((id) => announcements.some((a) => a.id === id))))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [announcements])

  if (!current) return null

  const badge = TYPE_BADGE[current.type] ?? TYPE_BADGE.Info

  // Se la conferma non è obbligatoria, chiudere con la X equivale ad averlo visto (segna come
  // letto). Se è obbligatoria, la X lo scarta solo per questa sessione: ricompare al prossimo
  // accesso finché non si preme esplicitamente "Ho letto".
  const handleDismiss = () => {
    if (!current.requireAcknowledgment) {
      acknowledgeMutation.mutate(current.id)
    } else {
      setDismissedIds((prev) => new Set(prev).add(current.id))
    }
  }

  return (
    <Modal
      show
      onClose={handleDismiss}
      title={current.title}
      size="md"
      footer={
        <Button
          variant="primary"
          icon={<CheckCircle size={16} />}
          disabled={acknowledgeMutation.isPending}
          onClick={() => acknowledgeMutation.mutate(current.id)}
        >
          Ho letto
        </Button>
      }
    >
      <div className="mb-3 flex items-center gap-2">
        <Megaphone size={16} className="text-faint" />
        <StatusBadge color={badge.color} label={badge.label} solid />
      </div>
      {current.summary && <p className="mb-2 font-medium text-body">{current.summary}</p>}
      <p className="m-0 whitespace-pre-wrap text-sm text-body">{current.content}</p>
    </Modal>
  )
}
